/**
 * Shows a frame response that does not contain its frame, instead of losing it.
 *
 * A link or form inside a `<turbo-frame>` gets a response, and Turbo drops it when it holds no frame
 * with that id: the frame collapses or reads "Content missing", and only the console says why.
 * A 500 inside a frame is the same case, because the error page has no frame. `turbo:fetch-request-error`
 * does not fire for it; it fires only when no response arrives, which this does not cover.
 *
 * Cancelling `turbo:frame-missing` stops Turbo's "Content missing" and its thrown `TurboFrameMissingError`.
 * The response then renders as a full page through Turbo's own `detail.visit`, so a different page opens,
 * a server error shows the error page, and a rejected form shows its validation messages.
 *
 * Turbo never raises `turbo:frame-missing` for a response with no body at all: it ignores it without a
 * trace. That is the empty 403 after a forced sign-out, so a second listener watches the responses to
 * frame requests and, for a refused one with no body, visits the current location again. The reload
 * leads to sign-in. A body that is only whitespace does raise the event and takes the same reload.
 *
 * There is no per-frame opt-out and no environment flag. One `console.error` per mismatch names the
 * frame and the URL, so a browser pass and the HealthCheck smoke crawler catch it.
 */

import { session } from "@hotwired/turbo";
import type { TurboBeforeFetchRequestEvent, TurboBeforeFetchResponseEvent, TurboFrameMissingEvent } from "@hotwired/turbo";

// The frame id of each in-flight frame request, by the element Turbo dispatches its events on (a frame, or a form inside one).
const frameRequests = new WeakMap<EventTarget, string>();

function reportMismatch(frameId: string | undefined, response: Response): void {
   console.error(
      `Turbo frame mismatch: the response (${response.status}) from ${response.url} did not contain <turbo-frame id="${frameId}">.`,
   );
}

function reloadCurrentPage(): void {
   session.visit(window.location.href, { action: "replace" });
}

// A failed body read is not swallowed: the rejection reaches the console, and the page is left as it is.
function onFrameMissing(event: TurboFrameMissingEvent): void {
   const { response, visit } = event.detail;
   event.preventDefault();
   reportMismatch((event.target as Element | null)?.id, response);

   void response
      .clone()
      .text()
      .then((body) => {
         if (body.trim() === "") {
            reloadCurrentPage();
         } else {
            // Turbo's `visit` takes no options for a Response; its declared type asks for them.
            void visit(response, {});
         }
      });
}

function onBeforeFetchRequest(event: TurboBeforeFetchRequestEvent): void {
   if (!event.target) {
      return;
   }
   const frameId = new Headers(event.detail?.fetchOptions?.headers).get("Turbo-Frame");
   if (frameId) {
      frameRequests.set(event.target, frameId);
   } else {
      frameRequests.delete(event.target);
   }
}

function onBeforeFetchResponse(event: TurboBeforeFetchResponseEvent): void {
   const response = event.detail?.fetchResponse?.response;
   const frameId = event.target ? frameRequests.get(event.target) : undefined;
   if (!response || !frameId || response.ok) {
      return;
   }

   // Exactly empty, not whitespace-only: Turbo raises `turbo:frame-missing` for a whitespace-only body, and
   // `onFrameMissing` reports and reloads for it. A refused response with a body is not a mismatch until
   // `onFrameMissing` says so, so this handler reports only the empty one, or the one it cannot read.
   void response
      .clone()
      .text()
      .then(
         (body) => {
            if (body === "") {
               reportMismatch(frameId, response);
               reloadCurrentPage();
            }
         },
         (error: unknown) => {
            // Still names the frame, then the rejection reaches the console and the page is left as it is.
            reportMismatch(frameId, response);
            throw error;
         },
      );
}

/** Installs the document listeners. Returns an uninstall function (tests). */
export function installTurboFrameMissingHandler(): () => void {
   document.addEventListener("turbo:frame-missing", onFrameMissing);
   document.addEventListener("turbo:before-fetch-request", onBeforeFetchRequest);
   document.addEventListener("turbo:before-fetch-response", onBeforeFetchResponse);

   return () => {
      document.removeEventListener("turbo:frame-missing", onFrameMissing);
      document.removeEventListener("turbo:before-fetch-request", onBeforeFetchRequest);
      document.removeEventListener("turbo:before-fetch-response", onBeforeFetchResponse);
   };
}
